import { Badge, Floating, Table, TableCell, TableCellDate, TableRow, TableRowSkeleton } from '@/ui'
import { generatePath, useParams } from 'react-router-dom'
import { useCampaignVersionsQuery } from '@/layouts/campaigns/api/useCampaignVersionsQuery'
import { LIVEOPS_DETAILS_PATH, LIVEOPS_VERSION_ITEM_PATH } from '@/libs'
import { useTranslation } from 'react-i18next'
import { Button, ChevronDown, DotsHorizontal, Edit03, Menu, MenuItem, Popper, useModal } from '@dashboard/ui'
import { Campaign, VersionRead } from '@/api/dashboard'
import { RenameVersionModal } from '@/layouts/campaigns/widgets/RenameVersionModal'
import { useCurrentUser } from '@/api/useCurrentUser'
import { useCurrentCampaignVersionQuery } from '@/layouts/campaigns/api/useCurrentCampaignVersionQuery'

interface CampaignVersionsTableProps {
  campaign: Campaign
  items: VersionRead[]
  isLoading: boolean
  refetch: () => void
  onClose?: () => void
}

export const CampaignVersionsTable = ({ isLoading, items, refetch, onClose, campaign }: CampaignVersionsTableProps) => {
  const { t } = useTranslation()
  const { canEdit } = useCurrentUser()

  const { companyId, gameId, eventId, campaignVersionId } = useParams() as {
    companyId: string
    gameId: string
    eventId: string
    campaignVersionId: string
  }
  const { data: currentVersion } = useCurrentCampaignVersionQuery(companyId, gameId, eventId)

  const openRenameVersion = useModal<{ version: VersionRead }>(props => (
    <RenameVersionModal
      {...props}
      version={props.version}
      onClose={() => {
        refetch()
        props.onClose?.()
      }}
    />
  ))

  return (
    <div className="max-h-[500px] min-h-[300px] w-[600px] overflow-auto rounded-xl border border-border-primary bg-fg-primary p-4">
      <div className=" mb-3 text-paragraph-md font-medium text-text-primary">{t('campaign.versions')}</div>

      {isLoading && <TableRowSkeleton rowCount={10} columnCount={2} />}

      {!isLoading && (
        <Table>
          <TableRow
            className={!campaignVersionId ? 'bg-fg-gray-secondary' : ''}
            onClick={() => {
              onClose?.()
            }}
            to={generatePath(LIVEOPS_DETAILS_PATH, { companyId, gameId, eventId })}
          >
            <TableCell>
              <div className="mb-1 font-semibold text-text-secondary">
                {campaign.version}
                <Badge variant="brand-blue-tertiary" className="ml-1">
                  {t('campaign.versions.latest')}
                </Badge>
              </div>
              <div className="truncate whitespace-nowrap text-text-tertiary-hover">{campaign.version_description}</div>
            </TableCell>
            <TableCell width={200}>
              <span className="truncate whitespace-nowrap">
                {currentVersion ? currentVersion.customer_email || currentVersion.customer_name : ''}
              </span>
              {currentVersion && <TableCellDate timestamp={currentVersion.created_at} options={{ time: true }} />}
            </TableCell>
            {items.length > 0 && <TableCell width={60} />}
          </TableRow>

          {items.map(it => (
            <TableRow
              key={it.id}
              id={it.id}
              className={it.id == campaignVersionId ? 'bg-fg-gray-secondary' : ''}
              onClick={() => {
                onClose?.()
              }}
              to={generatePath(LIVEOPS_VERSION_ITEM_PATH, { companyId, gameId, eventId, campaignVersionId: it.id })}
            >
              <TableCell>
                <div className="mb-1 font-semibold text-text-secondary">{it.version}</div>
                <div className="truncate whitespace-nowrap text-text-tertiary-hover">{it.version_description}</div>
              </TableCell>
              <TableCell width={200}>
                <span className="truncate whitespace-nowrap">{it.customer_email || it.customer_name}</span>
                <span className="truncate whitespace-nowrap">
                  <TableCellDate timestamp={it.created_at} options={{ time: true }} />
                </span>
              </TableCell>
              <TableCell width={60}>
                {canEdit && (
                  <div
                    className="text-right"
                    onClick={e => {
                      e.stopPropagation()
                      e.preventDefault()
                    }}
                  >
                    <Popper
                      popper={p => (
                        <Menu {...p}>
                          <MenuItem
                            icon={Edit03}
                            onClick={() => {
                              openRenameVersion({ version: it })
                              p.onClose()
                              onClose?.()
                            }}
                          >
                            {t('campaign.versions.edit-description')}
                          </MenuItem>
                        </Menu>
                      )}
                    >
                      <Button variant="tertiary" color="secondary">
                        <DotsHorizontal />
                      </Button>
                    </Popper>
                  </div>
                )}
              </TableCell>
            </TableRow>
          ))}
        </Table>
      )}
    </div>
  )
}

export const CampaignVersionButton = (props: { campaign: Campaign }) => {
  const { companyId, gameId, eventId, campaignVersionId } = useParams() as {
    companyId: string
    gameId: string
    eventId: string
    campaignVersionId: string
  }
  const {
    data: items = [],
    isLoading,
    refetch,
  } = useCampaignVersionsQuery(companyId, gameId, eventId, {
    offset: 0,
    limit: 100,
  })

  const getVersionName = () => {
    if (campaignVersionId) {
      const version = items.find(it => it.id === campaignVersionId)
      if (version) {
        return version.version
      }
    }

    return props.campaign?.version || ''
  }

  return (
    <Floating
      menu={<CampaignVersionsTable campaign={props.campaign} items={items} isLoading={isLoading} refetch={refetch} />}
      placement="bottom"
      referenceWidth={true}
    >
      <Button variant="tertiary" className="text-text-secondary">
        Version
        <span className="text-text-brand-solid">{getVersionName()}</span>
        <ChevronDown />
      </Button>
    </Floating>
  )
}
